import { ApiError, request, requestRaw } from '@/api/client'
import { env } from '@/config/env'

/**
 * The company a sign-in belongs to (owner: some companies sign in on their own link with no code; others type the
 * 6-digit company code once, it is remembered on this device, and they can change it).
 */
export interface SavedCompany {
  code: string
  name: string | null
  logo: string | null
  loginUrl: string | null
}

export interface GatewayCompany {
  code: string | null
  name: string | null
  logo: string | null
  loginMode: 'direct' | 'code'
}

export interface CompanyLookup {
  found: boolean
  name?: string | null
  logo?: string | null
  loginUrl?: string | null
}

export const COMPANY_KEY = 'corex-company'
export const isCompanyCode = (v: string) => /^[0-9]{6}$/.test(v)

export function readSavedCompany(): SavedCompany | null {
  try {
    const raw = localStorage.getItem(COMPANY_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<SavedCompany>
    if (!v || typeof v.code !== 'string' || !isCompanyCode(v.code)) return null
    return { code: v.code, name: v.name ?? null, logo: v.logo ?? null, loginUrl: v.loginUrl ?? null }
  } catch {
    return null
  }
}

export function saveCompany(c: SavedCompany) {
  try {
    localStorage.setItem(COMPANY_KEY, JSON.stringify(c))
  } catch {
    /* storage unavailable: the code is asked again next time */
  }
}

export function clearSavedCompany() {
  try {
    localStorage.removeItem(COMPANY_KEY)
  } catch {
    /* nothing kept */
  }
}

/** The absolute address of another origin's sign-in page, or null when it is this origin (or not a web address). */
export function otherOrigin(loginUrl: string | null | undefined): string | null {
  if (!loginUrl) return null
  try {
    const url = new URL(loginUrl, window.location.href)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    return url.origin === window.location.origin ? null : url.href
  } catch {
    return null
  }
}

export const companyService = {
  async current(): Promise<GatewayCompany> {
    // without the gateway: the backend names the company of this host; no company here means the code is asked
    if (env.apiMode === 'direct') {
      try {
        return (await request<GatewayCompany>('/backend/web/company', { timeoutMs: 8000 })).data
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return { code: null, name: null, logo: null, loginMode: 'code' }
        throw err
      }
    }
    return (await request<GatewayCompany>('/auth/company', { timeoutMs: 8000 })).data
  },
  async lookup(code: string): Promise<CompanyLookup> {
    if (env.apiMode === 'direct') {
      // the backend's public lookup answers the body itself, not an envelope
      const res = await requestRaw('/public/v1/company-lookup', { query: { code }, timeoutMs: 10000, absolute: true })
      const body = (await res.json().catch(() => null)) as CompanyLookup | null
      return { found: Boolean(body?.found), name: body?.name ?? null, logo: body?.logo ?? null, loginUrl: body?.loginUrl ?? null }
    }
    return (await request<CompanyLookup>('/auth/company-lookup', { query: { code }, timeoutMs: 10000 })).data
  },
}

/** Leaving for another company's sign-in page (one place, so a test can watch it). */
export const companyNav = {
  go(url: string) {
    window.location.assign(url)
  },
}
